import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { TableType } from '../enums/TableType';
import { AppSettings } from '../models/AppSettings.model';
import Currency from '../models/Currency.model';
import Floor from '../models/Floor.model';
import Language from '../models/Language.model';
import { Coordinates } from '../models/Settings.model';
import Table from '../models/Table.model';
import Venue from '../models/Venue.model';
import { evaluateOperatingHours } from '../utility/operating-hours.utils';
import { PBService } from './pocketbase.service';

@Injectable({ providedIn: 'root' })
export class AppSettingsService {
  private PB = inject(PBService);
  private translate = inject(TranslateService);
  private router = inject(Router);

  public settingsLoaded = signal<boolean>(false);
  public tableID = signal<string | null>(null);
  public currentTable = signal<Table | null>(null);
  public floorID = signal<string | null>(null);
  public selectedCurrency = signal<Currency | undefined>(undefined);
  public availableLanguages = signal<Language[]>([]);
  public activeMenuLanguage = signal<Language | undefined>(undefined);
  public initialSelectedCurrency = signal<Currency | undefined>(undefined);
  public initialAvailableLanguages = signal<Language[]>([]);

  // Application & Geofence Settings
  public appSettings = signal<AppSettings>(new AppSettings());
  public coordinates = signal<Coordinates | null>(null);

  // QR Menu Order Mode Computeds
  public qrMenuSettings = computed(() => this.appSettings().QRMenu);
  public tableOrderEnabled = computed(() => this.qrMenuSettings().TableOrder.Enabled);
  public pickupOrderEnabled = computed(() => this.qrMenuSettings().PickupOrder.Enabled);
  public locationCheckSettings = computed(() => this.qrMenuSettings().LocationCheck);
  public tableCallEnabled = computed(() => this.qrMenuSettings().TableCall);
  public isOrderingEnabled = computed(() => this.tableOrderEnabled() || this.pickupOrderEnabled());
  public isCatalogOnly = computed(() => !this.isOrderingEnabled());

  // Operating Hours (UTC0) & Order Limits Computeds
  public operatingStatus = computed(() => evaluateOperatingHours(this.qrMenuSettings().OperatingHours));
  public isRestaurantOpen = computed(() => this.operatingStatus().isOpen);
  public orderLimits = computed(() => this.qrMenuSettings().OrderLimits);
  public minOrderTotal = computed(() => this.orderLimits()?.MinOrderTotal ?? null);
  public maxOrderTotal = computed(() => this.orderLimits()?.MaxOrderTotal ?? null);

  private lastLoadedCustomerID: string | undefined = undefined;
  public venue: Venue | undefined;
  public get customer(): Venue | undefined { return this.venue; }
  public set customer(v: Venue | undefined) { this.venue = v; }

  async resolvePricingTier(): Promise<void> {
    const tID = this.tableID();
    const fID = this.floorID();
    let resolvedTier: TableType = TableType.TABLE;

    try {
      if (tID) {
        const table: Table | null = await this.PB.getTable(tID);
        this.currentTable.set(table);
        if (table && table.Type !== undefined && table.Type !== null) {
          resolvedTier = table.Type as TableType;
        }
      } else {
        this.currentTable.set(null);
        if (fID) {
          const floor: Floor | null = await this.PB.getFloor(fID);
          if (floor && floor.Type !== undefined && floor.Type !== null) {
            resolvedTier = floor.Type as TableType;
          }
        }
      }
    } catch (e) {
      console.error('Error resolving pricing tier:', e);
    }

    this.PB.pricingTier.set(resolvedTier);
  }

  async loadSettings(customerID: string): Promise<void> {
    if (!customerID || this.lastLoadedCustomerID === customerID) {
      return;
    }

    this.settingsLoaded.set(false);
    this.lastLoadedCustomerID = customerID;

    await this.resolvePricingTier();

    try {
      const settings = await this.PB.getSettings(customerID);
      if (settings && settings.length > 0) {
        const currentSettings = settings[0];
        const currencyObj = currentSettings.DefaultCurrency || currentSettings.Currency;
        this.selectedCurrency.set(currencyObj);
        this.initialSelectedCurrency.set(currencyObj);

        const languages: Language[] =
          currentSettings.Language && Array.isArray(currentSettings.Language) && currentSettings.Language.length > 0
            ? currentSettings.Language
            : [
              ...(currentSettings.DefaultLanguage ? [currentSettings.DefaultLanguage] : []),
              ...(currentSettings.AllowedLanguages || []),
            ];
        this.availableLanguages.set(languages);
        this.initialAvailableLanguages.set([...languages]);
        this.setDefaultActiveLanguage(languages);
        this.venue = currentSettings.Venue || currentSettings.Customer;
        this.customer = this.venue;

        // Parse AppSettings & Coordinates
        const appSettingsObj = new AppSettings(currentSettings.AppSettings);
        this.appSettings.set(appSettingsObj);
        this.coordinates.set(currentSettings.Coordinates ?? null);

        // Check if user has an active QRMenu Licence (QR Menu Family 20-29)
        const hasQRMenuLicence = (this.venue || this.customer)?.Licences?.some(
          (licence: any) =>
            (typeof licence.Type === 'number' && Math.floor(licence.Type / 10) === 2)
        );
        if (!hasQRMenuLicence) {
          this.router.navigate(['/'], { queryParamsHandling: 'preserve' });
          return;
        }

      } else {
        this.resetSettings();
      }

      this.settingsLoaded.set(true);
    } catch (error) {
      console.error('Error loading settings:', error);
      this.resetSettings();
      this.settingsLoaded.set(false);
    }
  }

  private setDefaultActiveLanguage(availableLanguages: Language[]) {
    if (availableLanguages.length === 0) {
      this.activeMenuLanguage.set(undefined);
      return;
    }

    const currentAppLang = this.translate.currentLang;
    const matchingLang = availableLanguages.find(
      (lang) => lang.Code.toLowerCase() === currentAppLang.toLowerCase()
    );

    if (matchingLang) {
      this.activeMenuLanguage.set(matchingLang);
    } else {
      this.activeMenuLanguage.set(availableLanguages[0]);
    }
  }

  public setActiveMenuLanguage(langId: string) {
    const selectedLang = this.availableLanguages().find((lang) => lang.id === langId);
    if (selectedLang) {
      this.activeMenuLanguage.set(selectedLang);
      const langCode = selectedLang.Code.split('-')[0].toLowerCase();
      this.translate.use(langCode);
    } else {
      console.warn(
        `Attempted to set active menu language to ID '${langId}', which is not available.`
      );
    }
  }

  private resetSettings() {
    this.selectedCurrency.set(undefined);
    this.availableLanguages.set([]);
    this.activeMenuLanguage.set(undefined);
    this.initialSelectedCurrency.set(undefined);
    this.initialAvailableLanguages.set([]);
    this.appSettings.set(new AppSettings());
    this.coordinates.set(null);
    this.lastLoadedCustomerID = undefined;
  }
}
